// Native side of the phone line (Android: android/app/.../PhonePlugin.java):
// push token for ringing a closed app, notification permission, and what the
// user tapped on the incoming-call notification. Every call is a safe no-op
// in the browser or on a platform without the plugin (iOS until CallKit).
import { registerPlugin } from '@capacitor/core';
import { isNativeApp, nativePlatform } from './native';

const RunvyaPhone = registerPlugin('RunvyaPhone');

// Android: PhonePlugin.java (FCM); iOS: RunvyaPhonePlugin.swift (PushKit / CallKit).
const available = () => isNativeApp() && ['android', 'ios'].includes(nativePlatform());

const safe = async (fn, fallback = null) => {
  if (!available()) return fallback;
  try {
    return await fn();
  } catch {
    return fallback;
  }
};

export const getPushToken = () => safe(async () => (await RunvyaPhone.getPushToken()).token || null);

// What the phone registration (PUT /api/telephony/mobile/device) needs from a
// plugin token result / "pushToken" event: Android an FCM push_token, iOS a
// PushKit voip_token (+ sandbox for debug builds).
export const pushFields = (r) => {
  if (!r || !r.token) return {};
  return r.voip ? { voip_token: r.token, voip_sandbox: !!r.sandbox } : { push_token: r.token };
};

export const getPushRegistration = () => safe(async () => pushFields(await RunvyaPhone.getPushToken()), {});

export const requestNotificationPermission = () => safe(
  async () => !!(await RunvyaPhone.requestNotificationPermission()).granted, false,
);

export const requestMicrophonePermission = () => safe(
  async () => !!(await RunvyaPhone.requestMicrophonePermission()).granted, false,
);

// {action: 'answer' | 'open', call_id} the app was opened with, once.
export const takeCallAction = () => safe(async () => {
  const r = await RunvyaPhone.takeCallAction();
  return r && r.call_id ? r : null;
});

// A call declined from the notification while the app was closed, once.
export const takeDeclined = () => safe(async () => (await RunvyaPhone.takeDeclined()).call_id || null);

// Call audio route: {active: true, speaker: false} = earpiece / headset,
// speaker: true = loudspeaker; {active: false} when the call ends.
export const setCallAudio = (opts) => safe(() => RunvyaPhone.setCallAudio(opts));

// Ringback ("çalıyor") tone for outgoing calls, on the call audio route.
export const setRingback = (on) => safe(() => RunvyaPhone.setRingback({ on }));

export const cancelCallNotification = () => safe(() => RunvyaPhone.cancelCallNotification());

// listener(data) for 'callAction' ({action, call_id}) and 'pushToken' ({token}).
// Returns a cleanup function.
export function onPhoneEvent(event, listener) {
  if (!available()) return () => {};
  const handle = RunvyaPhone.addListener(event, listener);
  return () => {
    Promise.resolve(handle).then((h) => h?.remove?.()).catch(() => {});
  };
}
